/**
 * Phase 61 Plan 06 — Category tag CRUD component.
 *
 * Shows all tags (including disabled). Each row has:
 *   - inline label rename (click → input → save)
 *   - inline weight edit
 *   - disable / reenable button (D-20 non-destructive)
 * "New tag" form at the bottom.
 */
import { useState } from 'react';
import type { CategoryTagDTO } from '../../../services/api/respectAdmin';
import {
  useCategoryTags,
  useCreateCategoryTag,
  useUpdateCategoryTag,
  useDisableCategoryTag,
  useReenableCategoryTag,
} from '../../../hooks/useRespectAdmin';

// ── Inline edit row ───────────────────────────────────────────────────────────

function TagRow({ tag }: { tag: CategoryTagDTO }) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(tag.label);
  const [editingWeight, setEditingWeight] = useState(false);
  const [weightDraft, setWeightDraft] = useState(String(tag.weight));

  const updateTag = useUpdateCategoryTag();
  const disableTag = useDisableCategoryTag();
  const reenableTag = useReenableCategoryTag();

  const isDisabled = tag.disabledAt !== null;

  function saveLabel() {
    const trimmed = labelDraft.trim();
    if (trimmed && trimmed !== tag.label) {
      updateTag.mutate({ id: tag.id, input: { label: trimmed } });
    }
    setEditingLabel(false);
    setLabelDraft(tag.label);
  }

  function saveWeight() {
    const parsed = parseInt(weightDraft, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed !== tag.weight) {
      updateTag.mutate({ id: tag.id, input: { weight: parsed } });
    }
    setEditingWeight(false);
    setWeightDraft(String(tag.weight));
  }

  return (
    <tr className={isDisabled ? 'bg-gray-50 opacity-60' : 'bg-white'}>
      {/* Label */}
      <td className="px-4 py-2 text-sm">
        {editingLabel ? (
          <input
            autoFocus
            className="border border-blue-400 rounded px-2 py-0.5 text-sm w-40"
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={saveLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveLabel();
              if (e.key === 'Escape') {
                setLabelDraft(tag.label);
                setEditingLabel(false);
              }
            }}
          />
        ) : (
          <span
            className="cursor-pointer hover:underline"
            onClick={() => {
              if (!isDisabled) {
                setLabelDraft(tag.label);
                setEditingLabel(true);
              }
            }}
            title={isDisabled ? 'Re-enable tag to rename' : 'Click to rename'}
          >
            {tag.label}
          </span>
        )}
      </td>

      {/* Weight */}
      <td className="px-4 py-2 text-sm text-center">
        {editingWeight ? (
          <input
            autoFocus
            type="number"
            min={0}
            className="border border-blue-400 rounded px-2 py-0.5 text-sm w-16 text-center"
            value={weightDraft}
            onChange={(e) => setWeightDraft(e.target.value)}
            onBlur={saveWeight}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveWeight();
              if (e.key === 'Escape') {
                setWeightDraft(String(tag.weight));
                setEditingWeight(false);
              }
            }}
          />
        ) : (
          <span
            className="cursor-pointer hover:underline"
            onClick={() => {
              if (!isDisabled) {
                setWeightDraft(String(tag.weight));
                setEditingWeight(true);
              }
            }}
            title={isDisabled ? 'Re-enable tag to edit weight' : 'Click to edit weight'}
          >
            {tag.weight}
          </span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-2 text-sm text-center">
        {isDisabled ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
            Disabled
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            Active
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="px-4 py-2 text-sm text-right">
        {isDisabled ? (
          <button
            className="text-blue-600 hover:text-blue-800 text-xs disabled:opacity-50"
            disabled={reenableTag.isPending}
            onClick={() => reenableTag.mutate(tag.id)}
          >
            Re-enable
          </button>
        ) : (
          <button
            className="text-red-600 hover:text-red-800 text-xs disabled:opacity-50"
            disabled={disableTag.isPending}
            onClick={() => disableTag.mutate(tag.id)}
          >
            Disable
          </button>
        )}
      </td>
    </tr>
  );
}

// ── New tag form ──────────────────────────────────────────────────────────────

function NewTagForm() {
  const [label, setLabel] = useState('');
  const [weight, setWeight] = useState('1');
  const createTag = useCreateCategoryTag();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;
    const w = parseInt(weight, 10);
    createTag.mutate(
      { label: trimmed, weight: isNaN(w) || w < 0 ? 1 : w },
      {
        onSuccess: () => {
          setLabel('');
          setWeight('1');
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
      <input
        className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
        placeholder="New tag label (e.g. Mentorship)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        maxLength={64}
      />
      <input
        type="number"
        min={0}
        className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
        placeholder="Weight"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
      />
      <button
        type="submit"
        disabled={createTag.isPending || !label.trim()}
        className="bg-primary-600 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 disabled:opacity-50"
      >
        {createTag.isPending ? 'Adding…' : 'Add tag'}
      </button>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function CategoryTagEditor() {
  const { data: tags, isLoading, isError } = useCategoryTags();

  if (isLoading) return <p className="text-sm text-gray-500">Loading tags…</p>;
  if (isError) return <p className="text-sm text-red-600">Failed to load category tags.</p>;

  return (
    <div>
      <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 font-medium text-gray-700">Label</th>
            <th className="px-4 py-2 font-medium text-gray-700 text-center">Weight</th>
            <th className="px-4 py-2 font-medium text-gray-700 text-center">Status</th>
            <th className="px-4 py-2 font-medium text-gray-700 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tags?.map((tag) => <TagRow key={tag.id} tag={tag} />)}
          {tags?.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-4 text-center text-gray-400 text-sm">
                No category tags yet. Add one below.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <NewTagForm />
    </div>
  );
}
